import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import DOMPurify from 'dompurify';

import drupalPubSubService from '../../services/drupalPubSub/drupalPubSub';
import ProgressBar from '../../components/ProgressBar';
import useDrupalSettings from '../../hooks/useDrupalSettings';
import {
  setPromoBannerCurrentQualifiedPercentages,
  setPromoBannerCurrentTier,
  setPromoBannerEstimatedQualifiedPercentages,
  setPromoBannerRemaingAmount
} from '../../slices/cart';

import giftIconSvg from '../../assets/images/svg/gift-icon.svg';
import {
  PROMO_BANNER_DARK_COLOR,
  PROMO_BANNER_DETAILS_MODAL_LINK,
  PROMO_BANNER_LANDING_PAGE_TYPE,
  PROMO_BANNER_LINK_BUTTON,
  PROMO_BANNER_PRODUCT_PAGE_TYPE,
  convertCentAmountToDollar,
  convertDifferenceToPercentage,
  convertDollarToCentAmount,
  getCurrentTier
} from './constants';
import styles from './PromoBannerTracker.module.scss';
import messages from './messages';

import { getLastURIComponent, calculateTotalDiscounts } from '../../utils';

const PromoBannerTracker = ({
  hasEstimatedValue = false,
  isCartView = false,
  isMobileCartView = false,
  isModal = false,
  progressBarEnabled = false,
}) => {
  const { sanitize } = DOMPurify;
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const { promotionalBanner } = useDrupalSettings();
  const {
    lineItems,
    totalPrice: discountedTotalPrice,
    promoBanner,
  } = useSelector((state) => state.cart);
  const { pdpEstimatedTotal } = useSelector((state) => state.pdp);

  const isFreeGift = useMemo(() => promotionalBanner?.tiers?.length === 1 && promotionalBanner?.tiers?.[0].discount === '0', [promotionalBanner?.tiers]);
  const isPDP = PROMO_BANNER_PRODUCT_PAGE_TYPE.includes(promotionalBanner?.bundle);
  const isLandingPage = PROMO_BANNER_LANDING_PAGE_TYPE.includes(promotionalBanner?.bundle);

  const [isEmptyCart, setIsEmptyCart] = useState(false);

  const totalDiscount = useMemo(
    () => ({
      centAmount: calculateTotalDiscounts({ lineItems }),
      currencyCode: discountedTotalPrice?.currencyCode,
      fractionDigits: discountedTotalPrice?.fractionDigits,
    }),
    [
      discountedTotalPrice?.currencyCode,
      discountedTotalPrice?.fractionDigits,
      lineItems,
    ],
  );

  const totalPrice = {
    ...(discountedTotalPrice ?? {}),
    centAmount:
      (discountedTotalPrice?.centAmount ?? 0) +
      (totalDiscount?.centAmount ?? 0),
  };
  
  const detailsModalLink = promotionalBanner?.field_p_ctas?.find(
    (cta) => cta.cta_bundle === PROMO_BANNER_DETAILS_MODAL_LINK
  );

  const buttonLink = promotionalBanner?.field_p_ctas?.find(
    (cta) => cta.cta_bundle === PROMO_BANNER_LINK_BUTTON
  );

  const isOverTier = useCallback((tierIndex) => 
    convertCentAmountToDollar(
      totalPrice?.centAmount
    ) >= promotionalBanner?.tiers?.[tierIndex]?.tier,
  [promotionalBanner?.tiers, totalPrice?.centAmount]);  
  
  const isOverFirstTier = useMemo(() => isOverTier(0), [isOverTier]);
  const isOverCurrentTier = useMemo(() => 
    isOverTier(promoBanner?.currentTier),
    [promoBanner?.currentTier, isOverTier]);
  const isOverPreviousTier = useMemo(() => 
    isOverTier(promoBanner?.currentTier - 1) || isOverTier(0),
    [promoBanner?.currentTier, isOverTier]); 
  const isOverFinalTier = useMemo(() => {
    if (promoBanner?.currentTier === 3) {
      return isOverTier(3);
    }
    return false;
  }, [promoBanner?.currentTier, isOverTier]);

  const lastURIComponent = getLastURIComponent().split('?').shift();
  const isOrderConfirmationPage = lastURIComponent === 'order-confirmation';

  const tierBenchmarks = useMemo(() => 
    (promotionalBanner?.tiers || []).map(tier => convertDollarToCentAmount(tier?.tier || 0)), 
    [promotionalBanner?.tiers]
  );

  const adjustedPercentages = useMemo(() =>  {
    const initialPercentages =
      convertDifferenceToPercentage(totalPrice?.centAmount, tierBenchmarks);
    return initialPercentages.map((percentage, index, array) => {
      if (index > 0 && array[index - 1] < 100) {
        return 0;
      }
      return Math.min(percentage, 100);
    });
  },[totalPrice?.centAmount, tierBenchmarks]);

  const adjustedEstimatedPercentages = useMemo(() => {
    const adjustedEstimatedTotal =
      (pdpEstimatedTotal?.totalPrice?.centAmount || 0) + totalPrice?.centAmount;
    const initialEstimatedPercentages =
      convertDifferenceToPercentage(adjustedEstimatedTotal, tierBenchmarks);
    return initialEstimatedPercentages.map((percentage, index, array) => {
      if (index > 0 && array[index - 1] < 100) {
        return 0;
      }
      return Math.min(percentage, 100);
    });
  }, 
    [pdpEstimatedTotal?.totalPrice?.centAmount, tierBenchmarks, totalPrice?.centAmount]
  );

  const currentTier = useMemo(() => {
    if (totalPrice?.centAmount > 0) {
      return getCurrentTier(adjustedPercentages)
    }
    return 0;
  },
    [adjustedPercentages, totalPrice?.centAmount]
  );

  const finalPercentages = useMemo(() => 
    isEmptyCart
      ? { first: hasEstimatedValue ? 10 : 0, second: 0, third: 0, fourth: 0 }
      : {
          first: adjustedPercentages[0],
          second: adjustedPercentages[1],
          third: adjustedPercentages[2],
          fourth: adjustedPercentages[3]
        }, 
    [adjustedPercentages, isEmptyCart, hasEstimatedValue]
  );

  const finalEstimatedPercentages = useMemo(() => ({
    first: adjustedEstimatedPercentages[0],
    second: adjustedEstimatedPercentages[1],
    third: adjustedEstimatedPercentages[2],
    fourth: adjustedEstimatedPercentages[3]
  }), [adjustedEstimatedPercentages]);

  const remainingAmount = useMemo(() => {
    const tiers = promotionalBanner?.tiers || [];
    const currentTierData = tiers[currentTier] || {};
    const nextTierData = tiers[currentTier + 1] || {};
    
    const currentPercentage = adjustedPercentages[currentTier] || 0;
    const currentTierAmount = currentTierData.tier || 0;
    
    const tierAmount = currentPercentage === 100 ? nextTierData.tier || 0 : currentTierAmount;
    
    const totalAmountInDollars = convertCentAmountToDollar(totalPrice?.centAmount || 0);
    
    return tierAmount - totalAmountInDollars;
  }, [adjustedPercentages, promotionalBanner?.tiers, currentTier, totalPrice?.centAmount]);

  const dispatchPercentages = useCallback(() => {
    dispatch(setPromoBannerCurrentTier(currentTier));
    if (remainingAmount < 0) {
      dispatch(setPromoBannerRemaingAmount(0));
    } else {
      dispatch(setPromoBannerRemaingAmount(remainingAmount));
    }
    dispatch(setPromoBannerCurrentQualifiedPercentages(finalPercentages));
    if (hasEstimatedValue) {
      dispatch(setPromoBannerEstimatedQualifiedPercentages(finalEstimatedPercentages));
    }
  }, [
    currentTier,
    dispatch,
    finalEstimatedPercentages,
    finalPercentages,
    hasEstimatedValue,
    remainingAmount,
  ]);

  useEffect(() => {
    dispatchPercentages();
  }, [dispatchPercentages]);

  useEffect(() => {
    setIsEmptyCart(true);
    if (lineItems?.length > 0) {
      setIsEmptyCart(false);
    }
  }, [lineItems]);

  const onClickLink = () => {
    drupalPubSubService.publish({
      subscription: 'onShowModal',
      parameters: {
        micromodalTrigger: detailsModalLink?.modal_id,
      },
    });
  };

  return !isOrderConfirmationPage && promoBanner && promotionalBanner?.tiers ? (
    <div 
      className={classnames(styles['promo-banner-container'], {
        [styles['is-cart-view']]: isCartView,
        [styles['is-mobile-cart-view']]: isMobileCartView,
        [styles['is-free-gift']]: isFreeGift,
        [styles['is-modal']]: isModal,
        [styles['is-product-page']]: isPDP,
        [styles['has-bg-image']]: !!promotionalBanner?.bg_image && isLandingPage,
        [styles['has-promo-bg-color']]: promotionalBanner?.bg_color === PROMO_BANNER_DARK_COLOR && isLandingPage,
      })}
    >
      <div 
        className={
          classnames(styles['promo-banner-tracker-wrapper'],
          !!promotionalBanner?.bg_color && isLandingPage ? `color-brand-bg-${promotionalBanner?.bg_color}` : 'color-brand-bg-stardust', {
          [styles['has-estimated-value']]: hasEstimatedValue,
        })}
        style={{
          backgroundImage: !!promotionalBanner?.bg_image && isLandingPage ? `url('${promotionalBanner?.bg_image}')` : 'none'
        }}
      >
        {isFreeGift &&
        isLandingPage && !isPDP &&
        !isCartView && !isMobileCartView && (
          <div className={styles['promo-banner-eyebrow']}>
            {isEmptyCart && !isOverFirstTier && (
              formatMessage(messages.noItemsInBannerFreeGiftMessaging, {
                minimumSpendingAmount: promotionalBanner?.tiers?.[0]?.tier
              })
            )}
            {!isEmptyCart && !isOverFirstTier  && (
              formatMessage(messages.currentProgressInBannerFreeGiftMessaging, {
                remainingAmount: promoBanner?.remainingAmount,
              })
            )}
            {!isEmptyCart && isOverFirstTier && (
              formatMessage(messages.fullyQualifiedInBannerFreeGiftMessaging)
            )}
          </div>
        )}
        {!isFreeGift && !hasEstimatedValue && !isCartView && !isMobileCartView && (
          <div className={styles['promo-banner-eyebrow']}>
            {isEmptyCart && (
              formatMessage(messages.noItemsInCartMessaging, {
                totalSavingsAmount: promotionalBanner?.tiers?.[3]?.discount
              })
            )}
            {!isEmptyCart && !isOverFinalTier  && (
              formatMessage(messages.currentQualifiedAmountMessaging, {
                spendingAmount: promoBanner?.remainingAmount,
                savingsAmount: promotionalBanner?.tiers?.[promoBanner?.currentTier+1]?.discount,
              })
            )}
            {!isEmptyCart && isOverFinalTier && (
              formatMessage(messages.fullyQualifiedAmountMessaging, {
                finalSavings: promotionalBanner?.tiers?.[3]?.discount,
              })
            )}
          </div>
        )}
        <div className={classnames(styles['promo-banner-header-and-subtext'], {
          [styles['has-authored-subtext']]: promotionalBanner?.subtext
        })}>
          {isFreeGift && !isPDP && !isModal && !isCartView && !isMobileCartView && (
            <div className={styles['promo-banner-header']}>{promotionalBanner?.headline}</div>
          )}
          {isFreeGift && (isModal || isCartView || isMobileCartView) && (
            <div className={styles['promo-banner-header']}>
              {(isEmptyCart || (!isEmptyCart && !isOverFirstTier)) && (
                formatMessage(messages.freeGiftHeadlineInCart)
              )}
              {!isEmptyCart && isOverFirstTier && (
                formatMessage(messages.congratsFreeGiftHeadlineInCart)
              )}
            </div>
          )}
          {!isFreeGift && (
            <div className={styles['promo-banner-header']}>{promotionalBanner?.headline}</div>
          )}
          {isFreeGift && isLandingPage && promotionalBanner?.subtext && (
            <div className={styles['promo-banner-subtext']}>
              {promotionalBanner?.subtext}
            </div>
          )}
          {isFreeGift && ((isPDP && isModal) || isCartView || isMobileCartView) && (
            <div className={styles['promo-banner-subtext']}>
              {isEmptyCart && !isOverFirstTier && (
                formatMessage(messages.noItemsInCartFreeGiftMessaging, {
                  remainingAmount: promoBanner?.remainingAmount,
                  // TODO: replace this with real product
                  freeGiftName: promotionalBanner?.product_name,
                })
              )}
              {!isEmptyCart && !isOverFirstTier && (
                formatMessage(messages.currentProgressInCartFreeGiftMessaging, {
                  remainingAmount: promoBanner?.remainingAmount,
                  // TODO: replace this with real product
                  freeGiftName: promotionalBanner?.product_name,
                })
              )}
              {!isEmptyCart && isOverFirstTier && (
                formatMessage(messages.fullyQualifiedInCartFreeGiftMessaging, {
                  freeGiftName: promotionalBanner?.product_name,
                })
              )}
              {!isModal && detailsModalLink && (
                <span
                  className={styles['promo-banner-details-link']}
                  onClick={onClickLink}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onClickLink();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  {detailsModalLink.label}
                </span>
              )}
            </div>
          )}
          {isFreeGift && isPDP &&
          (!isModal || !hasEstimatedValue) && !isCartView && !isMobileCartView && (
            <div className={classnames(styles['promo-banner-subtext'], styles['promo-banner-flex-subtext'])}>
              <img
                className={styles['promo-banner-free-gift-icon']}
                alt={formatMessage(messages.freeGiftIconAlt)}
                src={giftIconSvg}
              />
              <span className={styles['promo-banner-subtext-copy']}>
                {(isEmptyCart && !isOverFirstTier) && (
                  formatMessage(messages.noItemsInPDPFreeGiftMessaging, {
                    minimumSpendingAmount: promoBanner?.remainingAmount,
                    freeGiftName: promotionalBanner?.product_name,
                  })
                )}
                {!isEmptyCart && !isOverFirstTier && (
                  formatMessage(messages.currentProgressInPDPFreeGiftMessaging, {
                    remainingAmount: promoBanner?.remainingAmount,
                    freeGiftName: promotionalBanner?.product_name
                  })
                )}
                {!isEmptyCart && isOverFirstTier && (
                  formatMessage(messages.fullyQualifiedInPDPFreeGiftMessaging, {
                    freeGiftName: promotionalBanner?.product_name
                  })
                )}
              </span>
            </div>
          )}
          {!isFreeGift && (hasEstimatedValue || isCartView || isMobileCartView) && (
            <div className={styles['promo-banner-subtext']}>
              {(isEmptyCart || 
                (!isEmptyCart && !isOverFirstTier && !isMobileCartView && !isCartView)
              ) && (
                formatMessage(messages.noItemsPDPMessaging, {
                  savingsAmount: promotionalBanner?.tiers?.[3]?.discount,
                })
              )}
              {(isCartView || isMobileCartView) &&
              !isEmptyCart && !isOverFirstTier && (
                formatMessage(messages.almostQualifiedAmountCartMessaging, {
                  remainingAmount: promoBanner?.remainingAmount,
                  currentTierDiscount: 
                    promotionalBanner?.tiers?.[promoBanner?.currentTier]?.discount,
                })
              )}
              {!isEmptyCart &&
                (isOverCurrentTier || isOverPreviousTier) && !isOverFinalTier &&
                !isModal &&
                !isCartView && !isMobileCartView && (
                formatMessage(messages.currentQualifiedAmountPDPMessaging, {
                  currentSavings: promotionalBanner?.tiers?.[promoBanner?.currentTier]?.discount,
                  savingsAmount:
                    promotionalBanner?.tiers?.[
                      promoBanner?.currentTier < 3 ?
                        promoBanner?.currentTier + 1 : promoBanner?.currentTier
                    ]?.discount,
                })
              )}
              {!isEmptyCart && isOverCurrentTier &&
              !isOverFinalTier && isModal &&
              !isCartView && !isMobileCartView  && (
                formatMessage(messages.currentQualifiedAmountModalMessaging, {
                  currentSavings: promotionalBanner?.tiers?.[promoBanner?.currentTier]?.discount,
                  remainingAmount,
                  savingsAmount: promotionalBanner?.tiers?.[promoBanner?.currentTier + 1]?.discount,
                })
              )}
              {!isEmptyCart &&
              (isOverCurrentTier || isOverPreviousTier) &&
              !isOverFinalTier && (isCartView || isMobileCartView)    && (
                formatMessage(messages.currentQualifiedAmountCartMessaging, {
                  currentSavings: promotionalBanner?.tiers?.[promoBanner?.currentTier]?.discount,
                  remainingAmount,
                  savingsAmount:
                    promotionalBanner?.tiers?.[
                      promoBanner?.currentTier < 3 ?
                        promoBanner?.currentTier + 1 : promoBanner?.currentTier
                    ]?.discount,
                })
              )}
              {!isEmptyCart &&
              isOverFinalTier &&
              PROMO_BANNER_LANDING_PAGE_TYPE.includes(promotionalBanner?.bundle) && (
                formatMessage(messages.fullyQualifiedAmountPDPMessaging, {
                  finalSavings: promotionalBanner?.tiers?.[3]?.discount,
                })
              )}
              {!isEmptyCart && isOverFinalTier &&
              !PROMO_BANNER_LANDING_PAGE_TYPE.includes(promotionalBanner?.bundle) && (
                formatMessage(messages.fullyQualifiedAmountPDPMessaging, {
                  finalSavings: promotionalBanner?.tiers?.[3]?.discount,
                })
              )}
              {!isModal && detailsModalLink && (
                <span
                  className={styles['promo-banner-details-link']}
                  onClick={onClickLink}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onClickLink();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  {detailsModalLink.label}
                </span>
              )}
            </div>
          )}
          {!promotionalBanner?.subtext &&
            !hasEstimatedValue &&
            !isCartView &&
            !isMobileCartView &&
            buttonLink && (
            <div
              className={styles['promo-banner-button-link']}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitize(buttonLink.html),
              }}
            />
          )}
        </div>
        {progressBarEnabled && (
          <div className={styles['promo-banner-tracker']}>
            <ProgressBar
              hasEstimatedProgress={hasEstimatedValue}
              tiers={promotionalBanner?.tiers}
            />
          </div>
        )}
        <div className={styles['promo-banner-links']}>
        {promotionalBanner?.subtext &&
          !hasEstimatedValue &&
          !isCartView &&
          !isMobileCartView &&
          buttonLink && (
            <div
              className={styles['promo-banner-button-link']}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitize(buttonLink.html),
              }}
            />
          )}
          {!hasEstimatedValue &&
            !isCartView &&
            !isMobileCartView &&
            detailsModalLink && (
            <button
              type='button'
              className={styles['promo-banner-details-link']}
              onClick={(e) => {
                e.preventDefault();
                onClickLink();
              }}
              onKeyDown={(e) => {
                e.preventDefault();
                if (e.key === 'Enter') {
                  onClickLink();
                }
              }}
              aria-label='details modal link'
            >
              {detailsModalLink.label}
            </button>
          )}
        </div>
      </div>
    </div>
  ) : null;
};

PromoBannerTracker.propTypes = {
  hasEstimatedValue: PropTypes.bool,
  isCartView: PropTypes.bool,
  isMobileCartView: PropTypes.bool,
  isModal: PropTypes.bool,
  progressBarEnabled: PropTypes.bool,
};

export default PromoBannerTracker;